import {
  Container,
  Title,
  Table,
  Text,
  Group,
  Card,
  Badge,
  Center,
  Loader,
  Stack,
  Alert,
  TextInput,
  Select,
  Button,
  Modal,
} from '@mantine/core';
import {
  IconUsers,
  IconAlertCircle,
  IconSearch,
  IconDownload,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useVolunteers } from '../../../hooks/useVolunteer';
import { adminApi, type VolunteerResponse } from '../../../api/volunteer';
import { BackLink } from '@/components/BackLink';

export default function Volunteers() {
  const { t } = useTranslation();
  const { data: volunteers, isLoading, error } = useVolunteers();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>('all');
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [archiveDownloading, setArchiveDownloading] = useState(false);

  const downloadArchive = async () => {
    setArchiveDownloading(true);
    try {
      const { blob, filename } = await adminApi.downloadVolunteersArchive();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setArchiveModalOpen(false);
      notifications.show({
        color: 'green',
        title: t('admin.volunteers.archive.successTitle'),
        message: t('admin.volunteers.archive.successMessage'),
      });
    } catch {
      notifications.show({
        color: 'red',
        title: t('common.error'),
        message: t('admin.volunteers.archive.error'),
      });
    } finally {
      setArchiveDownloading(false);
    }
  };

  const filteredVolunteers = useMemo(() => {
    if (!volunteers) return [];

    return volunteers.filter((volunteer: VolunteerResponse) => {
      // Search filter
      const searchLower = search.toLowerCase();
      const matchesSearch =
        !search ||
        volunteer.firstName.toLowerCase().includes(searchLower) ||
        volunteer.lastName.toLowerCase().includes(searchLower) ||
        volunteer.email.toLowerCase().includes(searchLower);

      // Status filter
      let matchesStatus = true;
      if (statusFilter === 'hasAccount') {
        matchesStatus = !!volunteer.user;
      } else if (statusFilter === 'noAccount') {
        matchesStatus = !volunteer.user;
      } else if (statusFilter === 'contractSigned') {
        matchesStatus = volunteer.contractSigned;
      } else if (statusFilter === 'contractPending') {
        matchesStatus = !volunteer.contractSigned;
      }

      return matchesSearch && matchesStatus;
    });
  }, [volunteers, search, statusFilter]);

  if (isLoading) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (error) {
    return (
      <Container size="md" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title={t('common.error')}
          color="red"
        >
          {t('admin.volunteers.error')}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <BackLink to="/app/dashboard" />
      <Group justify="space-between" mb="xl">
        <Group>
          <IconUsers size={32} />
          <Title order={2}>{t('admin.volunteers.title')}</Title>
        </Group>
        <Group>
          <Button
            leftSection={<IconDownload size={18} />}
            variant="light"
            onClick={() => setArchiveModalOpen(true)}
          >
            {t('admin.volunteers.archive.action')}
          </Button>
          <Badge size="lg" variant="light" color="blue">
            {volunteers?.length || 0} {t('admin.volunteers.total')}
          </Badge>
        </Group>
      </Group>

      <Card withBorder padding="md" mb="lg" radius="md">
        <Group>
          <TextInput
            placeholder={t('admin.volunteers.search')}
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder={t('admin.volunteers.filterStatus')}
            value={statusFilter}
            onChange={setStatusFilter}
            data={[
              { value: 'all', label: t('admin.volunteers.filters.all') },
              {
                value: 'hasAccount',
                label: t('admin.volunteers.filters.hasAccount'),
              },
              {
                value: 'noAccount',
                label: t('admin.volunteers.filters.noAccount'),
              },
              {
                value: 'contractSigned',
                label: t('admin.volunteers.filters.contractSigned'),
              },
              {
                value: 'contractPending',
                label: t('admin.volunteers.filters.contractPending'),
              },
            ]}
            w={200}
          />
        </Group>
      </Card>

      {filteredVolunteers.length === 0 ? (
        <Card withBorder padding="xl" radius="md">
          <Stack align="center" gap="md">
            <IconUsers size={48} color="gray" />
            <Text c="dimmed" ta="center">
              {search || statusFilter !== 'all'
                ? t('admin.volunteers.noResults')
                : t('admin.volunteers.empty')}
            </Text>
          </Stack>
        </Card>
      ) : (
        <Card withBorder padding={0} radius="md">
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('admin.volunteers.table.name')}</Table.Th>
                <Table.Th>{t('admin.volunteers.table.email')}</Table.Th>
                <Table.Th>{t('admin.volunteers.table.phone')}</Table.Th>
                <Table.Th>{t('admin.volunteers.table.contract')}</Table.Th>
                <Table.Th>{t('admin.volunteers.table.account')}</Table.Th>
                <Table.Th ta="center">
                  {t('admin.volunteers.table.attendance')}
                </Table.Th>
                <Table.Th>{t('admin.volunteers.table.registeredAt')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredVolunteers.map((volunteer: VolunteerResponse) => (
                <Table.Tr key={volunteer.id}>
                  <Table.Td>
                    <Link
                      to={`/app/admin/volunteers/${volunteer.id}`}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      <Text fw={500} c="blue" style={{ cursor: 'pointer' }}>
                        {volunteer.firstName} {volunteer.lastName}
                      </Text>
                    </Link>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {volunteer.email}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {volunteer.phone}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {volunteer.contractSigned ? (
                      <Badge color="green" variant="light">
                        {t('admin.volunteers.status.signed')}
                      </Badge>
                    ) : (
                      <Badge color="yellow" variant="light">
                        {t('admin.volunteers.status.pending')}
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {volunteer.user ? (
                      <Badge color="blue" variant="light">
                        {t('admin.volunteers.status.hasAccount')}
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light">
                        {t('admin.volunteers.status.noAccount')}
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td ta="center">
                    <Badge color="teal" variant="light">
                      {volunteer.attendanceTotal}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {new Date(volunteer.createdAt).toLocaleDateString()}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}

      <Modal
        opened={archiveModalOpen}
        onClose={() => setArchiveModalOpen(false)}
        title={t('admin.volunteers.archive.dialog.title')}
        centered
      >
        <Stack>
          <Text>{t('admin.volunteers.archive.dialog.message')}</Text>
          <Text size="sm" c="dimmed">
            {t('admin.volunteers.archive.dialog.details')}
          </Text>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setArchiveModalOpen(false)}
              disabled={archiveDownloading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              leftSection={<IconDownload size={18} />}
              onClick={downloadArchive}
              loading={archiveDownloading}
            >
              {t('admin.volunteers.archive.dialog.confirm')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
